import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useFetch, apiRequest } from "../api/baseApi";
import { AuthContext } from "./AuthContext";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

export const ClientContext = createContext();

export const ClientProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);

  const [selectedClient, setSelectedClient] = useState();
  const [selectedCaregiver, setSelectedCaregiver] = useState();
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(false);
  const [isDeletingClient, setIsDeletingClient] = useState(false);
  const [isDeletingCaregiver, setIsDeletingCaregiver] = useState(false);

  const { clientId: clientIdParam, caregiverId: caregiverIdParam } =
    useParams();

  const clientUrl = useMemo(() => {
    if (currentUser?.isAdmin || currentUser?.isSuperAdmin) {
      return "clients";
    }
    if (currentUser?.id) {
      return `caregivers/${currentUser.id}/clients`;
    }
    return null;
  }, [currentUser]);

  const {
    data: clients,
    loading: clientsLoading,
    refetch: refetchClients,
  } = useFetch(
    clientUrl,
    { method: "GET", version: "v1" },
    [clientUrl],
  );

  // Issue #339: previously fetched unconditionally on mount, so a visitor
  // with no session (e.g. commandcenter demo, MFA-blocked login) still fired
  // an authenticated request that 401'd and surfaced as an unhandled
  // rejection. Gate on currentUser like clientUrl above and CallV3Context.
  const caregiversUrl = useMemo(
    () => (currentUser?.id ? "caregivers" : null),
    [currentUser],
  );

  const { data: caregivers, refetch: refetchCaregivers } = useFetch(
    caregiversUrl,
    { method: "GET", version: "v1" },
    [caregiversUrl],
  );

  const changeSelectedClient = (clientId) => {
    setSelectedClient(
      clients.find((client) => client.id === parseInt(clientId)) || null,
    );
  };

  const changeSelectedCaregiver = (caregiverId) => {
    setSelectedCaregiver(
      caregivers.find((caregiver) => caregiver.id === parseInt(caregiverId)) ||
        null,
    );
  };

  useEffect(() => {
    if (clients) {
      changeSelectedClient(selectedClient?.id);
    }
  }, [clients]);

  useEffect(() => {
    if (!clients || !clientIdParam) {
      return;
    }
    changeSelectedClient(clientIdParam);
  }, [clients, clientIdParam]);

  useEffect(() => {
    if (!caregivers || !caregiverIdParam) {
      return;
    }
    changeSelectedCaregiver(caregiverIdParam);
  }, [caregivers, caregiverIdParam]);

  const generateNewClientPassword = async (clientId) => {
    setIsGeneratingPassword(true);
    try {
      const response = await apiRequest(
        `/clients/${clientId}/create-password`,
        {
          method: "GET",
          version: "v2",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate new password");
      }

      const data = await response.json();
      return data.password;
    } catch (error) {
      toast.error(error.message || "Error generating new password");
      throw error;
    } finally {
      setIsGeneratingPassword(false);
    }
  };

  const registerClient = async (clientData) => {
    try {
      const params = {
        email: clientData.email,
        firstName: clientData.firstName,
        lastName: clientData.lastName,
        idNumber: clientData.idNumber,
        phone: clientData.phone,
        address: clientData.address,
        city: clientData.city,
        zipCode: clientData.zipCode,
        state: clientData.state,
        timezone: clientData.timezone,
      };

      const response = await apiRequest("/clients/register", {
        method: "POST",
        body: params,
        version: "v2",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to register client");
      }

      const data = await response.json();
      refetchClients();

      return {
        user: data.client,
        tempPassword: data.password,
      };
    } catch (error) {
      toast.error(error.message || "Error registering client");
      throw error;
    }
  };

  const deleteClient = async (clientId) => {
    try {
      setIsDeletingClient(true);
      const response = await apiRequest(`/users/${clientId}`, {
        method: "DELETE",
        version: "v2",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete client");
      }

      refetchClients();
      setSelectedClient(null);
      toast.success("Client deleted successfully");

      return true;
    } catch (error) {
      toast.error(error.message || "Error deleting client");
      throw error;
    } finally {
      setIsDeletingClient(false);
    }
  };

  const deleteCaregiver = async (caregiverId) => {
    try {
      setIsDeletingCaregiver(true);
      const response = await apiRequest(`/users/${caregiverId}`, {
        method: "DELETE",
        version: "v2",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete caregiver");
      }

      refetchCaregivers();
      setSelectedCaregiver(null);
      toast.success("Caregiver deleted successfully");

      return true;
    } catch (error) {
      toast.error(error.message || "Error deleting caregiver");
      throw error;
    } finally {
      setIsDeletingCaregiver(false);
    }
  };

  return (
    <ClientContext.Provider
      value={{
        clients,
        clientsLoading,
        refetchClients,
        selectedClient,
        changeSelectedClient,
        caregivers,
        changeSelectedCaregiver,
        selectedCaregiver,
        refetchCaregivers,
        generateNewClientPassword,
        isGeneratingPassword,
        registerClient,
        deleteClient,
        deleteCaregiver,
        isDeletingClient,
        isDeletingCaregiver,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};
